import { z } from "zod";

import { type OrganizationId, type PersonId } from "./mandate";

/**
 * IAM model — aligned with IAM-CDS token structure. Four persona roles,
 * side-based reach, named permissions. No delegation (recorded decision
 * 2026-06-17 in docs/iam-model.md).
 *
 * Every role arrives as an org-instance role string from IAM-CDS:
 *   `org.{orgId}.trexpert-portal.{persona}`
 *
 * The Identity type is constructed directly from the IAM token's `identity`
 * block; in dev/BDD the in-memory adapter builds the same shape from seeded
 * role strings.
 */

/**
 * Boundary schema for the raw identity token on the authenticated namespace.
 * Today (decision D2) the token is the persona's `PersonId` carried in the
 * `x-trexpert-person` header; on staging it becomes a real IAM-CDS bearer
 * token. Either way the resolver must validate it at the boundary rather than
 * cast a raw header string — a non-empty, length-bounded value with no control
 * characters. `parse` throws on a malformed/oversized token.
 */
export const IdentityTokenSchema = z
    .string()
    .trim()
    .min(1)
    // Generous upper bound: the in-memory persona token is a short PersonId, but
    // a real IAM-CDS JWT (many roles/claims) can exceed 4096 bytes.
    .max(8192)
    .regex(/^[\u0021-\u007e]+$/, "Token enthält ungültige Zeichen");

/** The four IAM-CDS persona roles under the `trexpert-portal` application. */
export const PERSONAS = ["trexpert-admin", "trexpert-clerk", "customer-admin", "customer-clerk"] as const;
export type Persona = (typeof PERSONAS)[number];

/** Which side of the Treuhand / customer relationship this persona acts on. */
export type Side = "trexpert" | "customer";

/** Derive the side from a persona. */
export const sideOf = (persona: Persona): Side => (persona.startsWith("trexpert-") ? "trexpert" : "customer");

/**
 * German display labels for the persona roles — the single source for both i18n
 * setups (the client touchpoints and the plain-DOM demo-app host shell), so the
 * persona switcher and any future role display stay identical without a copied
 * `navigation.json` block drifting. UI strings are German by rule; keys are the
 * technical persona ids.
 */
export const PERSONA_ROLE_LABELS_DE: Readonly<Record<Persona, string>> = {
    "trexpert-admin": "Trexpert-Administrator",
    "trexpert-clerk": "Trexpert-Sachbearbeiter",
    "customer-admin": "Administrator",
    "customer-clerk": "Sachbearbeiter",
};

/**
 * Named permissions that gate administrative actions beyond side + reach.
 * See docs/iam-model.md § "Question 2 — named permissions".
 */
export const PERMISSIONS = [
    "create-mandate",
    "invite-org-member",
    "administer-message-templates",
    "administer-link-list",
    "administer-mandate-settings",
] as const;
export type Permission = (typeof PERMISSIONS)[number];

/** Which named permissions each persona holds. */
export const PERSONA_PERMISSIONS: Readonly<Record<Persona, ReadonlySet<Permission>>> = {
    "trexpert-admin": new Set([
        "create-mandate",
        "invite-org-member",
        "administer-message-templates",
        "administer-link-list",
    ]),
    "trexpert-clerk": new Set(["create-mandate"]),
    "customer-admin": new Set(["invite-org-member", "administer-mandate-settings"]),
    "customer-clerk": new Set(["administer-mandate-settings"]),
};

/**
 * The IAM-CDS application ID used as the middle segment in role strings.
 * Role format: `org.{orgId}.{APP_ID}.{persona}`
 */
export const TREXPERT_APP_ID = "trexpert-portal";

/**
 * Parsed result from an IAM-CDS org-instance role string.
 * Role format: `org.{orgId}.trexpert-portal.{persona}`
 */
export interface ParsedRole {
    organizationId: string;
    persona: Persona;
}

/**
 * Parse an IAM-CDS org-instance role string into its parts.
 * Expected format: `org.{orgId}.trexpert-portal.{persona}`
 * Returns undefined if the string doesn't match the expected format or
 * contains an unknown persona.
 */
export const parseRole = (role: string): ParsedRole | undefined => {
    const prefix = `org.`;
    if (!role.startsWith(prefix)) {
        return undefined;
    }
    const suffix = `.${TREXPERT_APP_ID}.`;
    const appIdx = role.indexOf(suffix, prefix.length);
    if (appIdx < 0) {
        return undefined;
    }
    const organizationId = role.slice(prefix.length, appIdx);
    if (!organizationId) {
        return undefined;
    }
    const personaCandidate = role.slice(appIdx + suffix.length);
    if (!(PERSONAS as readonly string[]).includes(personaCandidate)) {
        return undefined;
    }
    return { organizationId, persona: personaCandidate as Persona };
};

/**
 * Extract the first matching trexpert-portal role from a list of IAM-CDS
 * role strings. Returns the parsed role or undefined if none match.
 */
export const personaFromRoles = (roles: readonly string[]): ParsedRole | undefined => {
    for (const role of roles) {
        const parsed = parseRole(role);
        if (parsed) {
            return parsed;
        }
    }
    return undefined;
};

export type OrganizationKind = "trexpert" | "customer";

export interface Organization {
    id: OrganizationId;
    name: string;
    kind: OrganizationKind;
}

/**
 * Seed data shape for a person in the in-memory adapter. Carries IAM-CDS
 * role strings instead of group names, so the in-memory path constructs
 * Identity the same way a real JWT would.
 */
export interface Person {
    id: PersonId;
    name: string;
    email: string;
    organizationId: OrganizationId;
    /** IAM-CDS `resourceAccess["trexpert-portal"].roles` strings. */
    roles: string[];
}

/**
 * The resolved identity the server reasons over per request. Constructed
 * from the IAM-CDS token's `identity` block (or the equivalent in-memory
 * seed data). The AuthorizationGuard consumes `persona`, `side`, and
 * `organizationId` to decide side + reach + optional named permission.
 */
export interface Identity {
    userId: PersonId;
    name: string;
    email: string;
    organizationId: OrganizationId;
    persona: Persona;
    side: Side;
}

/** Whether the identity belongs to the trexpert (trustee) side. */
export const isTrustee = (identity: Pick<Identity, "side">): boolean => identity.side === "trexpert";

/**
 * A trustee as offered for the «Zuständige Person»-Auswahl: a Trexpert staff
 * member (TrexpertAdmin or TrexpertClerk) reduced to the id and display name a
 * picker needs. The full {@link Person} carries email/roles the picker never
 * shows; this is the bounded option facet returned by `listTrustees`.
 */
export interface TrusteeOption {
    id: PersonId;
    name: string;
}

/**
 * Choose an organisation's primary notification recipient among its members:
 * prefer a member with the `customer-admin` role, otherwise fall back to the
 * first member. The preference order is a domain rule, not an adapter detail.
 */
export const selectPrimaryRecipient = <T extends { roles: readonly string[] }>(
    members: readonly T[]
): T | undefined =>
    members.find(member => member.roles.some(r => parseRole(r)?.persona === "customer-admin")) ?? members[0];

/**
 * The first name to greet a person by («Guten Tag {Vorname}»): the first
 * whitespace-delimited token of their display name. A single-word name is its
 * own first name; an empty or blank name yields `""` (the greeting then degrades
 * to «Guten Tag» without a trailing name rather than emitting a stray token).
 */
export const firstNameOf = (name: string): string => name.trim().split(/\s+/)[0] ?? "";
